import { type HTMLAttributes, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css, ThemeProvider } from 'styled-components';
import {
    BorderRadiusMedium,
    BorderRadiusSmall,
    ColorNeutralDarkGrey,
    ColorNordicGrey5,
    ColorOpacityLight3,
    FontBody2WeightLight,
    FontLineHeightNone,
    FontSize12,
    FontSize18,
    FontSize28,
    FontSize48,
    Spacing2,
    Spacing4,
    Spacing6,
    Spacing8,
    Spacing12,
    Spacing16,
    Spacing24,
    Spacing32
} from 'autogen/design-tokens/tokens';
import { useJavascriptRequestDataModelQuery } from 'autogen/swagger/all/hooks';
import { Body } from 'components/generic/typography';
import { em } from 'modules/helpers/style';
import { breakpoint, sizes } from 'variables';
import { calculateTimeLeft, generateNumbers, generateTranslations, timeDifference } from './shared/helpers';
import type { ICountdown } from './shared/types';

const Wrapper = styled.span`
    display: inline-flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    line-height: ${FontLineHeightNone};
    color: ${({ theme }) => theme.ColorTextPrimary};

    ${({ theme }) =>
        (theme as any).sizeType === 'M' &&
        css`
            background: ${ColorOpacityLight3};
            border-radius: ${BorderRadiusMedium};
            padding: ${Spacing16} ${Spacing32};

            ${breakpoint.up(sizes.md)} {
                max-width: 280px;
            }
        `}

    ${breakpoint.up(sizes.md)} {
        width: unset;
    }
`;

const StyledBody = styled(Body)`
    line-height: ${FontLineHeightNone};
    text-align: center;

    ${({ theme }) =>
        (theme as any).sizeType === 'M' &&
        css`
            margin-bottom: ${Spacing16};
        `}
`;

const Counter = styled.span`
    display: flex;
    align-items: flex-start;
`;

const Unit = styled.span`
    ${({ theme }) =>
        ((theme as any).sizeType === 'XS' &&
            css`
                display: flex;
                align-items: flex-end;

                & ~ & {
                    margin-left ${Spacing4};
                }
            `) ||
        ((theme as any).sizeType === 'M' &&
            css`
                text-align: center;
            `) ||
        (
            (theme as any).sizeType === 'XL' &&
                css`
                display: flex;
                flex-direction: column-reverse;
            `
        )}
`;

const NumberBlock = styled.span`
    display: flex;
`;

// biome-ignore lint/suspicious/noShadowRestrictedNames: <explanation>
const Number = styled.span`
    ${({ theme }) =>
        ((theme as any).sizeType === 'XS' &&
            css`
                width: ${em(9)}; // prevents size change
                text-align: center;
            `) ||
        ((theme as any).sizeType === 'M' &&
            css`
                width: 20px;
                font-size: ${FontSize18};
                padding: ${Spacing6} 0;
                border-radius: 2px;
                text-align: center;
                background: ${ColorNordicGrey5};
                margin: 0 ${Spacing2};
            `) ||
        (
            (theme as any).sizeType === 'XL' &&
                css`
                width: 35px;
                font-size: ${FontSize28};
                font-weight: ${FontBody2WeightLight};
                padding: ${Spacing12} 0;
                border-radius: ${BorderRadiusSmall};
                text-align: center;
                background: ${ColorNordicGrey5};
                margin: 0 ${Spacing2};

                ${breakpoint.up(sizes.md)} {
                    width: 45px;
                    font-size: ${FontSize48};
                }

                @media (max-width: 375px) {
                    width: 27px;
                    font-size: ${FontSize18};
                }
            `
        )}
`;

const TimeUnit = styled.span`
    ${({ theme }) =>
        (
            (theme as any).sizeType === 'M' &&
                css`
                font-size: ${FontSize12};
                margin: ${Spacing8} ${Spacing2} 0;
            `
        ) ||
        (
            (theme as any).sizeType === 'XL' &&
                css`
                font-size: ${FontSize12};
                margin: 0 ${Spacing2} ${Spacing8};
                color: ${ColorNeutralDarkGrey};
            `
        )}
`;

const Seperator = styled.span`
    ${({ theme }) =>
        ((theme as any).sizeType === 'XS' &&
            css`
                display: none;
            `) ||
        ((theme as any).sizeType === 'M' &&
            css`
                margin: ${Spacing8} ${Spacing2} 0;
            `) ||
        (
            (theme as any).sizeType === 'XL' &&
                css`
                margin: auto ${Spacing2} 14px;
                color: ${ColorNeutralDarkGrey};
                font-size: ${FontSize28};
                font-weight: ${FontBody2WeightLight};

                ${breakpoint.up(sizes.md)} {
                    margin: auto ${Spacing2} ${Spacing24};
                }

                @media (max-width: 375px) {
                    margin: auto ${Spacing2} ${Spacing12};
                    font-size: ${FontSize18};
                }
            `
        )}
`;

type CountdownProps = HTMLAttributes<HTMLDivElement> & {
    countdown: ICountdown;
};

export default function Countdown(props: CountdownProps) {
    const { countdown, className } = props;
    const {
        finishDateTime,
        currentTime,
        sizeType,
        text,
        showTimeUnits,
        dayToHourThredholdMsMilliseconds,
        displayType
    } = countdown;

    const { t } = useTranslation();
    const { data } = useJavascriptRequestDataModelQuery({ enabled: false });

    const updateInterval = 1000;
    const startDate = currentTime || data?.websiteTime.load;

    let millisecondsLeft = useMemo(() => timeDifference({ startDate, finishDateTime }), [startDate, finishDateTime]);
    const initialTime = useMemo(
        () => calculateTimeLeft(millisecondsLeft, displayType, dayToHourThredholdMsMilliseconds),
        []
    );
    const [time, setTime] = useState(initialTime);
    const { displayFormat } = time;

    const translationTimeStrings = generateTranslations(sizeType);
    const numbers = generateNumbers(sizeType, time);

    useEffect(() => {
        const interval = setInterval(() => {
            const timeObject = calculateTimeLeft(millisecondsLeft, displayType, dayToHourThredholdMsMilliseconds);

            requestAnimationFrame(() => setTime(timeObject));

            if (millisecondsLeft <= 0) {
                clearInterval(interval);
            } else {
                millisecondsLeft -= updateInterval;
            }
        }, updateInterval);

        return () => clearInterval(interval);
    }, [currentTime, finishDateTime, updateInterval]);

    return (
        <ThemeProvider theme={{ sizeType } as any}>
            <Wrapper className={className}>
                {sizeType === 'M' && !!text && (
                    <StyledBody size="M" strong forwardedAs="span">
                        {text}
                    </StyledBody>
                )}
                <Counter>
                    {displayFormat === 'days' && numbers.daysArray && (
                        <>
                            <Unit suppressHydrationWarning>
                                <NumberBlock>
                                    {numbers.daysArray.map((number, i) => (
                                        // eslint-disable-next-line react/no-array-index-key
                                        <Number key={i} suppressHydrationWarning>
                                            {number}
                                        </Number>
                                    ))}
                                </NumberBlock>
                                {showTimeUnits && (
                                    <TimeUnit suppressHydrationWarning>{t(translationTimeStrings.days)}</TimeUnit>
                                )}
                            </Unit>
                            <Seperator>:</Seperator>
                        </>
                    )}
                    {numbers.hoursArray && (
                        <>
                            <Unit suppressHydrationWarning>
                                <NumberBlock>
                                    {numbers.hoursArray.map((number, i) => (
                                        // eslint-disable-next-line react/no-array-index-key
                                        <Number key={i} suppressHydrationWarning>
                                            {number}
                                        </Number>
                                    ))}
                                </NumberBlock>
                                {showTimeUnits && (
                                    <TimeUnit suppressHydrationWarning>{t(translationTimeStrings.hours)}</TimeUnit>
                                )}
                            </Unit>
                            <Seperator>:</Seperator>
                        </>
                    )}
                    {numbers.minutesArray && (
                        <>
                            <Unit suppressHydrationWarning>
                                <NumberBlock>
                                    {numbers.minutesArray.map((number, i) => (
                                        // eslint-disable-next-line react/no-array-index-key
                                        <Number key={i} suppressHydrationWarning>
                                            {number}
                                        </Number>
                                    ))}
                                </NumberBlock>
                                {showTimeUnits && (
                                    <TimeUnit suppressHydrationWarning>{t(translationTimeStrings.minutes)}</TimeUnit>
                                )}
                            </Unit>
                            <Seperator>:</Seperator>
                        </>
                    )}
                    <Unit>
                        <NumberBlock>
                            {numbers.secondsArray.map((number, i) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <Number key={i} suppressHydrationWarning>
                                    {number}
                                </Number>
                            ))}
                        </NumberBlock>
                        {showTimeUnits && (
                            <TimeUnit suppressHydrationWarning>{t(translationTimeStrings.seconds)}</TimeUnit>
                        )}
                    </Unit>
                </Counter>
            </Wrapper>
        </ThemeProvider>
    );
}
